import type { GraphQLError, OperationDefinitionNode } from 'graphql';
import type { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';

const PERSISTED_QUERY_NOT_FOUND_CODE = 'PERSISTED_QUERY_HASH_NOT_FOUND';
const FORBIDDEN_CODE = 'FORBIDDEN';
const SENSITIVE_VARIABLE_NAME = /password|token/i;

export type GraphQLErrorLike = Partial<Pick<GraphQLError, 'extensions'>>;

export function isPersistedQueryNotFoundError(
  graphQLErrors?: ReadonlyArray<GraphQLErrorLike>,
): boolean {
  return Boolean(
    graphQLErrors?.some((error) => error.extensions?.code === PERSISTED_QUERY_NOT_FOUND_CODE),
  );
}

export function isEveryGraphQLErrorForbidden(
  graphQLErrors?: ReadonlyArray<GraphQLErrorLike>,
): boolean {
  return Boolean(
    graphQLErrors?.length &&
    graphQLErrors.every((error) => error.extensions?.code === FORBIDDEN_CODE),
  );
}

function redactSensitiveVariables(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactSensitiveVariables);
  if (value == null || typeof value !== 'object') return value;

  return Object.fromEntries(
    Object.entries(value).map(([name, nestedValue]) => [
      name,
      SENSITIVE_VARIABLE_NAME.test(name) ? '[redacted]' : redactSensitiveVariables(nestedValue),
    ]),
  );
}

// Apollo doesn't export the error-context type its onError handler receives,
// so derive it: the handler is onError's first parameter, and the context is
// the handler's first parameter.
type ApolloErrorContext = Parameters<Parameters<typeof onError>[0]>[0];

export function reportApolloErrorToSentry({
  graphQLErrors,
  networkError,
  operation,
}: Pick<ApolloErrorContext, 'graphQLErrors' | 'networkError' | 'operation'>): void {
  let message = '[Apollo Error]';
  if (networkError) {
    message = `[Apollo Network Error] ${networkError.message}`;
  } else if (graphQLErrors && graphQLErrors.length === 1) {
    message = `[Apollo GraphQL Error] ${graphQLErrors[0].message}`;
  } else if (graphQLErrors && graphQLErrors.length > 1) {
    message = `[Apollo GraphQL Errors] ${graphQLErrors.length} errors occurred`;
  }

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTags({
      apollo: true,
      operationName: operation.operationName,
      operationType: operation.query.definitions.find(
        (d): d is OperationDefinitionNode => d.kind === 'OperationDefinition',
      )?.operation,
    });
    scope.setExtras({
      graphQLErrors,
      networkError,
      query: operation.query.loc?.source.body,
      variables: redactSensitiveVariables(operation.variables),
    });
    Sentry.captureMessage(message);
  });
}
